// ProductDetails.js
import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import './ProductDetails.scss';
import { InteractionType } from '@azure/msal-browser';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { CatalogDataService } from '../../Services/CatalogsDataService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import noImage from '../../Assets/img/catalogs/no-product.png';

// Import chart components and register necessary Chart.js modules
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { instance, accounts, inProgress } = useMsal();
    const [product, setProduct] = useState(null);
    const fileInputRef = useRef(null);

    // Chart data states
    const [storePerfData, setStorePerfData] = useState([]);
    const [brandShareData, setBrandShareData] = useState(null);
    const [productTrendData, setProductTrendData] = useState([]);

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchProductDetails();
            fetchChartData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inProgress, accounts]);

    const fetchProductDetails = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const tokenResponse = await instance.acquireTokenSilent(request);
            const response = await CatalogDataService.getProductById(id, tokenResponse.accessToken);
            if (response && response.data) {
                setProduct(response.data);
            }
        } catch (error) {
            console.error('Error fetching product details:', error);
        }
    };

    const fetchChartData = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const tokenResponse = await instance.acquireTokenSilent(request);
            
            // Fetch Store Performance Data
            const spResponse = await CatalogDataService.getStorePerformance(id, tokenResponse.accessToken);
            if (spResponse && spResponse.data) {
                setStorePerfData(spResponse.data);
            }

            // Fetch Brand Share Data
            const bsResponse = await CatalogDataService.getBrandShare(id, tokenResponse.accessToken);
            if (bsResponse && bsResponse.data) {
                setBrandShareData(bsResponse.data);
            }

            // Fetch Product Trend Data
            const ptResponse = await CatalogDataService.getProductTrend(id, tokenResponse.accessToken);
            if (ptResponse && ptResponse.data) {
                setProductTrendData(ptResponse.data);
            }
        } catch (error) {
            console.error('Error fetching chart data:', error);
        }
    };

    // Trigger file input when "Edit Image" is clicked
    const handleEditImageClick = () => {
        fileInputRef.current.click();
    };

    // Handle file selection and upload
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const tokenResponse = await instance.acquireTokenSilent(request);
            // Call the API endpoint to update the product image
            const response = await CatalogDataService.updateProductImage(id, file, tokenResponse.accessToken);
            if (response && response.status === 200) {
                // Refresh product details to display the new image
                fetchProductDetails();
            }
        } catch (error) {
            console.error('Error updating product image:', error);
        }
    };

    if (!product) return <div>Loading...</div>;

    // Prepare chart data objects
    // const storePerfChartData = {
    //     labels: storePerfData.map(item => item.kpi),
    //     datasets: [
    //         {
    //             label: 'Sales',
    //             data: storePerfData.map(item => item.value),
    //             backgroundColor: ['#4caf50', '#f44336'],
    //         },
    //     ],
    // };

    const brandShareChartData = {
        labels: ['Brand Share', 'Remaining'],
        datasets: [
            {
                data: brandShareData ? [brandShareData.value, 100 - brandShareData.value] : [],
                backgroundColor: ['#2196f3', '#e0e0e0'],
            },
        ],
    };

    const productTrendChartData = {
        labels: productTrendData.map(item => item.period),
        datasets: [
            {
                label: 'Sales Amount',
                data: productTrendData.map(item => item.salesAmount),
                borderColor: '#ff9800',
                backgroundColor: 'rgba(255,152,0,0.5)',
                fill: true,
            },
        ],
    };

    return (
        <Container className="product-details">
            {/* Top left Back Button */}

            <div className="details-container">
                {/* Top Section (2/3 height) */}
                <div className="top-section">
                    {/* Left: Image (1/3 width) */}
                    <div className="image-section">
                        <div className='top-buttons d-flex'>
                            <Button variant="primary" size='lg' className="back-button text-white" onClick={() => navigate(-1)}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Button>
                        </div>
                        <div className='image-container'>
                        <img 
                            src={product.blobImageURL || noImage} 
                            alt={product.productName} 
                            onError={(e) => { e.target.onerror = null; e.target.src = noImage; }} 
                        />
                        </div>
                        <div className='d-flex justify-content-end'>
                        <Button variant="primary" className="edit-image-button" onClick={handleEditImageClick}>
                                Edit Image
                            </Button>
                            {/* Hidden file input */}
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>
                    </div>
                    {/* Right: General Information (2/3 width) */}
                    <div className="info-section">
                        <small className="subcategory text-muted">{product.subCategory}</small>
                        <h2 className="title">{product.productName}</h2>
                        <h3 className="subtitle">{product.size}</h3>
                        <strong>About this item</strong>
                        <ul className="attributes d-flex flex-column flex-wrap">
                            <li>SKU Code: {product.skuCode}</li>
                            <li>Barcode: {product.barcode}</li>
                            <li>Vendor: {product.vendorName}</li>
                            <li>Brand: {product.brand}</li>
                            <li>Category: {product.category}</li>
                            <li>Package Color: {product.packageColor}</li>
                            <li>Conversion Factor: {product.conversionFactor}</li>
                            <li>Cases: {product.cases}</li>
                            <li>Product Height: {product.productHeight}</li>
                            <li>Product Width: {product.productWidth}</li>
                            <li>Product Depth: {product.productDepth}</li>
                            <li>Weight: {product.weight}</li>
                            <li>Dimensions: {product.dimensions}</li>
                            <li>Manufacturer: {product.manufacturer}</li>
                            <li>Description: {product.productDescription}</li>
                            <li>PLU Code: {product.pluCode}</li>
                            <li>Parent Group: {product.parentGroup}</li>
                            <li>Created: {product.createdDate ? new Date(product.createdDate).toLocaleDateString() : ''}</li>
                            <li>Modified: {product.modifiedDate ? new Date(product.modifiedDate).toLocaleDateString() : ''}</li>
                        </ul>
                        <div className='d-flex justify-content-end'>
                        <Button variant="primary" className="edit-button">Edit</Button>
                        </div>
                    </div>
                </div>
                {/* Bottom Section (1/3 height) */}
                <div className="bottom-section">
                    <h4>More about this item</h4>
                    <div className="charts-container">
                        {/* <Card className="chart-card">
                            <h6>Store Performance</h6>
                            <Bar data={storePerfChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </Card> */}
                        <Card className="chart-card">
                            <h6>Store Performance</h6>
                            <div className="store-performance">
                                {storePerfData.map((item, index) => (
                                    <div key={index} className="store-performance-row">
                                        <span className="label">{item.kpi}:</span>
                                        <span className="value">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                        <Card className="chart-card">
                            <h6>Brand Share in Sub-Category</h6>
                            {brandShareData ? (
                                <Doughnut data={brandShareChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                            ) : (
                                <p>No data</p>
                            )}
                        </Card>
                        <Card className="chart-card">
                            <h6>Product Trend</h6>
                            <Line data={productTrendChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </Card>
                    </div>
                </div>
            </div>
        </Container>
    );
};

const ProductDetailsPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Product Details">
                <ProductDetails />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default ProductDetailsPage;
